import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

type OnboardingGuardMode = 'require-complete' | 'require-incomplete';

interface Props {
  mode: OnboardingGuardMode;
}

// Sebastian ruling 2026-09-15 round 3 — paths reachable regardless of
// onboardingCompleted. A book-only customer never completed audio
// onboarding but still needs to change his password (login hygiene)
// and delete his account (Art. 17 DSGVO — our privacy policy points
// at it). Every other /account/* path stays gated: subscription /
// children / library / notifications are audio-side surfaces and
// bouncing to /onboarding/welcome is the right behavior for a
// book-only customer who lands on them.
//
// Exported so AppShell can suppress its audio chrome (worlds row,
// BottomNav, EmailVerificationBanner) on these same two paths for a
// customer with no audio world — one source of truth for the routes
// a book-only customer is allowed to touch.
// Sebastian ruling 2026-09-15 round 5.
//
// Sebastian ruling 2026-09-25 — `/einloesen` added: a fresh gift
// recipient signing up via the link in the gift mail must reach the
// redeem page without being intercepted by the audio onboarding
// (vouchers are for books only). The returnTo path survives login +
// signup because `computePostAuthRedirect` honors any returnTo whose
// pathname is in this same set BEFORE the `!onboardingCompleted`
// check.
export const ONBOARDING_BYPASS_PATHS: ReadonlySet<string> = new Set([
  '/account/password',
  '/account/delete',
  '/einloesen',
  // Probecover Stage 1 (Sebastian 2026-10-15) — the lead-magnet
  // /figuren route reaches book-only customers who have no audio
  // world. Mirrors the existing `/einloesen` precedent: a
  // book-only / probe-only customer must not get bounced to the
  // audio onboarding on a returnTo bypass. The route itself sits
  // under BookShell (OUTSIDE OnboardingGuard), so this entry
  // governs the computePostAuthRedirect returnTo honoring only.
  '/figuren',
]);

export function OnboardingGuard({ mode }: Props) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  // Nested inside ProtectedRoute — if we get here with no user, the token
  // exists but /me hasn't hydrated yet. AppShell's hydration effect handles
  // that case with a spinner; on onboarding routes we just fail-safe by
  // rendering the outlet so the page at least mounts.
  if (!user) {
    return <Outlet />;
  }

  if (mode === 'require-complete' && !user.onboardingCompleted) {
    if (ONBOARDING_BYPASS_PATHS.has(location.pathname)) {
      return <Outlet />;
    }
    return <Navigate to="/onboarding/welcome" replace />;
  }

  if (mode === 'require-incomplete' && user.onboardingCompleted) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
